/* typesense-plp.js — Typesense-powered collection pages.
 * Mounts an InstantSearch facet rail + product grid over the Liquid PLP
 * (which stays as the crawler/no-JS fallback), scoped to the collection via
 * the `collections` facet. One engine with the search overlay: same cluster,
 * same vendored libs, same 3-second-fresh index. Facet UI uses the theme's
 * .plp/.facet specification-sheet classes (proto.css).
 *
 * 2026-09-09: Finish rail group, zero-hit ownership (the island keeps the
 * page on 0 hits for a filtered/sorted/queried landing; the Liquid grid is
 * for boot failure and the cold-index default landing only), Liquid empty
 * block retired on takeover. */
(() => {
  'use strict';
  const cfg = window.TYPESENSE;
  if (!cfg || !cfg.apiKey) return;
  if (/[?&]plp=react\b/.test(location.search)) return; // React island (island-plp.js) owns this page
  function runIsland(root) {
  if (root.__tsPlp) return;
  root.__tsPlp = true;
  // null → global grid (/search). Shopify's synthetic /collections/all has no
  // real collection behind it — no product carries handle "all", so filtering
  // on it returns zero results; treat it as the global grid too.
  let handle = root.getAttribute('data-collection-handle') || null;
  if (handle === 'all') handle = null;

  const h = (tag, cls, attrs, children) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (attrs) Object.keys(attrs).forEach((k) => n.setAttribute(k, attrs[k]));
    (children || []).forEach((c) => n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c));
    return n;
  };
  const money = (n) => { try { return Number(n).toLocaleString('en-AU', { style: 'currency', currency: cfg.currency || 'AUD' }); } catch { return '$' + n; } };

  // ---- sort menu ----------------------------------------------------------
  // Every value here is verified sortable against the live cluster. `title` is
  // indexed WITHOUT sort:true and there is no sales/popularity field, so A–Z /
  // Z–A / Best selling would return "Could not find a field named `title` in
  // the schema for sorting" — they are deliberately absent until the indexer
  // (shopify-typesense COLLECTION_SCHEMA) grows those fields. Flip `soon` off
  // and they appear; nothing else needs to change.
  // Kept byte-identical to SORTS in src/islands/plp/App.jsx.
  const SORTS = [
    { label: 'Featured', value: '' },
    { label: 'Price: low to high', value: 'price:asc' },
    { label: 'Price: high to low', value: 'price:desc' },
    { label: 'Newest', value: 'created_at:desc' },
    { label: 'Oldest', value: 'created_at:asc' },
    { label: 'Name A–Z', value: 'title:asc' },
    { label: 'Name Z–A', value: 'title:desc' },
    { label: 'Best selling', value: 'best_selling_rank:asc' },
  ];
  const LIVE_SORTS = SORTS.filter((s) => !s.soon);
  const isLiveSort = (v) => LIVE_SORTS.some((s) => s.value === v);

  // ---- merchandised blocks (docs/rfc-merchandised-blocks.md) -------------
  // WHICH blocks apply: snippets/plp-promos.liquid (window.ST_PLP_PROMOS).
  // WHERE they go: assets/plp-merch.js (window.STMerch), inlined before the
  // SSR grid — the same engine placed the Liquid grid, so the island's page
  // is the SSR page (same session seed, same context, same page number).
  //   TILE  one cell. Every page in this context carries STMerch.quota()
  //         tiles, so the query asks for CELLS - quota products and a full
  //         page is exactly CELLS cells (whole rows at 2/3/4/6/8 columns).
  //   BAND  a full row after `after` cells; plp-toolbar.js snaps it to the
  //         nearest row boundary at the live column count.
  const MERCH = window.STMerch || null;
  const CELLS = 24;
  let cellsPerPage = CELLS;
  const productsPerPage = (cells) => (MERCH ? MERCH.perPage({ collection: handle || '' }, cells) : cells);
  const merchCtx = (results) => {
    const st = (results && results._state) || {};
    const refs = (st.disjunctiveFacetsRefinements && st.disjunctiveFacetsRefinements.vendor)
      || (st.facetsRefinements && st.facetsRefinements.vendor) || [];
    return { collection: handle || '', query: (results && results.query) || '', vendors: refs };
  };
  function splicePromos(items, results) {
    if (!MERCH || !items.length) return items;
    const page = ((results && results.page) || 0) + 1;
    const plan = MERCH.forPage(merchCtx(results), page, items.length);
    return MERCH.splice(items, plan).map((x) => {
      if (x.hit) return x.hit;
      const b = x.tile || x.band;
      return { __promo: b.src, __after: x.band ? x.after : null, objectID: 'promo-' + b.id };
    });
  }

  const promoMedia = (p, html) => {
    if (p.kind === 'brand') {
      return html`<div class="promo__media promo__logo media">${p.logo
        ? html`<img src="${p.logo}" alt="${p.brand}" width="${p.logo_w}" height="${p.logo_h}" loading="lazy" decoding="async" />`
        : html`<span class="promo__wordmark">${p.brand}</span>`}</div>`;
    }
    return p.image ? html`<div class="promo__media media"><img src="${p.image}" alt="" width="${p.image_w}" height="${p.image_h}" loading="lazy" decoding="async" /></div>` : '';
  };
  const promoClass = (p) => 'promo promo--' + (p.style === 'banner' ? 'banner' : 'tile')
    + (p.kind === 'brand' ? ' promo--brand' + (p.logo_dark ? ' promo--logo-dark' : '') : '');
  const promoTemplate = (p, { html }, after) => html`
    <article class="${promoClass(p)}" data-merch-id="${p.handle}" data-merch-after="${after == null ? '' : String(after)}">
      ${promoMedia(p, html)}
      <div class="promo__body">
        <h3 class="promo__heading">${p.heading}</h3>
        ${p.body ? html`<p class="promo__text">${p.body}</p>` : ''}
        ${p.cta_label && p.cta_url
          ? html`<a class="promo__cta ${p.style === 'banner' ? 'promo__cta--link' : 'btn'}" href="${p.cta_url}">
              <span>${p.cta_label}</span>
              <svg class="promo__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>
            </a>`
          : ''}
      </div>
    </article>`;

  // The index stores cdn.shopify.com/s/files/… originals but Liquid emits
  // {shop}/cdn/shop/… — DIFFERENT cache keys for the same file. Normalise to
  // the Liquid form so tiles, PDP and quick view all share one cache.
  const cdn = (u, w) => (u
    ? u.replace(/^(https?:)?\/\/cdn\.shopify\.com\/s\/files\/1\/\d+\/\d+\/\d+\//, '//' + location.host + '/cdn/shop/')
      + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w
    : u);

  /* MEASURED tile geometry, byte-identical to snippets/grid-sizes.liquid
   * (layout: 'rail') and src/islands/plp/Tile.jsx. The island always renders
   * beside the 250px filter rail, so it only ever needs the rail string.
   *
  /* MEASURED tile geometry, byte-identical to snippets/grid-sizes.liquid
 * (layout: 'rail'). The island always renders beside the 250px filter rail.
 *
CORRECTED 2026-08-20: the PLP section is .collection--wide (max-width:none),
 * NOT .page-width, so the tile GROWS with the viewport instead of shrinking as
 * columns are added. Measured live: 289.2px at 1920 and 288.8px at 2560, where
 * the previous fixed-px bands were asking for 330px and 220px (0.91x and 0.61x
 * of the pixels the cover crop consumes). Above 1440 the answer is vw, not px. */
  const GRID_SIZES = '(min-width:3841px) 740px, (min-width:2600px) 20vw, (min-width:2048px) 25vw, (min-width:1440px) 36vw, (min-width:1001px) 44vw, (min-width:981px) 38vw, (min-width:681px) 44vw, 66vw';
  const GRID_WIDTHS = [200, 250, 310, 380, 460, 550, 660, 790, 940, 1100];
  const gridSrcset = (u) => GRID_WIDTHS.map((w) => cdn(u, w) + ' ' + w + 'w').join(', ');

  // (the finish-preview hover delegate lives in assets/product-card.js now —
  // it serves every surface that renders a card, not only this grid)

  // brand line -> brand-filtered PLP, in the island's own f=attr~value syntax
  // (decoded with decodeURIComponent, so spaces must be %20 not +)
  const vendorURL = (v) => ((window.routes && window.routes.all_products_url) || '/collections/all')
    + '?f=vendor~' + encodeURIComponent(v);
  // The PLP sections ship both libraries as deferred scripts, so by the time
  // this runs they are normally already there — only the usearch takeover (any
  // other template) still has to fetch them.
  const loadScript = (src, global) => (window[global] ? Promise.resolve() : new Promise((res, rej) => {
    const el = document.createElement('script');
    el.src = src; el.onload = res; el.onerror = rej;
    document.head.appendChild(el);
  }));

  async function boot() {
    // neither library imports the other, so when they DO have to be fetched
    // (takeover path) fetch them together — serialising 528KB then 145KB of
    // parse was pure dead time in front of the first search
    await Promise.all([
      loadScript(cfg.libs.is, 'instantsearch'),
      loadScript(cfg.libs.adapter, 'TypesenseInstantSearchAdapter'),
    ]);
    const instantsearch = window.instantsearch;
    const { hits, refinementList, rangeInput, stats, pagination, clearRefinements, configure, sortBy } = instantsearch.widgets;

    /* ---- the query chrome -------------------------------------------------
     * The search page's <h1>, its <title> and its empty-state line are Liquid,
     * rendered once from search.terms when the document was built. From then
     * on the island owns the query — the header search box is searchAsYouType
     * and writeURL() below only replaceState()s — so no navigation ever
     * happens and all three go on naming a term that has scrolled off into
     * history while the grid and the count are correct.
     *
     * Bound to the RESULTS' query, never to the input's value. connectStats
     * hands us results.query: the term that produced the hits currently on
     * screen. The input runs ahead of that by a debounce, so following it
     * would trade a stale heading for one that flickers through terms nobody
     * searched — the same lie at a different speed. Heading, count, grid and
     * empty state all land on one tick because they all come off one render.
     *
     * Only the search page carries [data-search-head]. On a collection PLP the
     * H1 names the collection, which a within-collection query does not change
     * (the scope chip says what is being searched), so this is inert there. */
    const qHead = document.querySelector('[data-search-head]');
    const fillQ = (tpl, term) => String(tpl || '').split('%q%').join(term);
    let lastChrome = null;
    function syncQueryChrome(term) {
      if (!qHead) return;
      term = String(term == null ? '' : term).trim();
      if (term === lastChrome) return;
      lastChrome = term;
      const d = qHead.dataset;
      const h1 = qHead.querySelector('h1');
      // textContent, not innerHTML: the term is whatever was typed
      if (h1) h1.textContent = term ? fillQ(d.tplHeading, term) : d.tplHeadingBlank;
      document.title = term ? fillQ(d.tplTitle, term) : d.tplTitleBlank;
    }

    const server = {
      apiKey: cfg.apiKey,
      nodes: [{ host: cfg.host, port: cfg.port || '443', protocol: cfg.protocol || 'https' }],
      cacheSearchResultsForSeconds: 120,
      // Pin the Typesense client onto axios's FETCH adapter (it picks XHR in
      // browsers by default). window.__TSX — installed at HTML parse by
      // snippets/search-provider.liquid — sits on fetch and answers the first
      // search from the request it already fired, and answers repeat visits
      // from sessionStorage. Intercepting the wire rather than the searchClient
      // means the adapter still does every response conversion itself.
      axiosAdapter: window.__TSX ? 'fetch' : undefined,
    };
    let adapter;
    const mkAdapter = (s) => new window.TypesenseInstantSearchAdapter({
      server: s,
      additionalSearchParameters: {
        query_by: 'title,vendor,skus,product_type,tags',
        highlight_full_fields: 'none',
      },
      collectionSpecificSearchParameters: {},
    });
    try {
      adapter = mkAdapter(server);
    } catch (e) {
      // an axios build without the fetch adapter: fall back to the default
      // transport. The parse-time search is simply not reused.
      adapter = mkAdapter(Object.assign({}, server, { axiosAdapter: undefined }));
    }
    const collection = cfg.collection || 'products';

    // ---- shell adoption ----------------------------------------------------
    // When the section shipped the island's chrome in the HTML (the [data-w]
    // mount points already exist — snippets/plp-rail-shell.liquid), ADOPT it:
    // the rail and the grid then paint in the same first frame and the widgets
    // only hydrate values into boxes that are already on screen. Roots without
    // it (the usearch takeover mounts a bare one) get the built island below.
    const adopted = root.hasAttribute('data-ts-shell');
    let island;
    if (adopted) {
      island = root;
    } else {
    // Build the island HIDDEN alongside the SSR Liquid content and only swap
    // once the first results have rendered — never blank out good content
    // while waiting on the network ("appeared then disappeared" bug).
    island = document.createElement('div');
    island.style.display = 'none';
    island.innerHTML = `
      <aside class="plp__rail" aria-label="Filters">
        <details class="plp__drawer" open>
          <summary class="plp__drawer-toggle btn btn--ghost">Filter</summary>
          <div class="plp__facets">
            <div data-w="clear"></div>
            <!-- TEMP(2026-09-08): Availability facet hidden — its only value is "Out of stock" while pre-launch inventory is placeholder zeros. Remove the hidden attr (and the matching guard in snippets/plp-rail-shell.liquid) to restore. Container stays in the DOM so the refinement widget still mounts. -->
            <fieldset class="facet" hidden><legend class="facet__name"><button type="button" class="facet__toggle" data-facet-toggle aria-expanded="true">Availability</button></legend><div class="facet__body" data-w="available"></div></fieldset>
            <fieldset class="facet"><legend class="facet__name"><button type="button" class="facet__toggle" data-facet-toggle aria-expanded="true">Features</button></legend><div class="facet__body" data-w="features"></div></fieldset>
            <fieldset class="facet"><legend class="facet__name"><button type="button" class="facet__toggle" data-facet-toggle aria-expanded="true">Brand</button></legend><div class="facet__body" data-w="vendor"></div></fieldset>
            <fieldset class="facet"><legend class="facet__name"><button type="button" class="facet__toggle" data-facet-toggle aria-expanded="true">Colour</button></legend><div class="facet__body" data-w="colours"></div></fieldset>
            <fieldset class="facet"><legend class="facet__name"><button type="button" class="facet__toggle" data-facet-toggle aria-expanded="true">Finish</button></legend><div class="facet__body" data-w="finishes"></div></fieldset>
            <fieldset class="facet"><legend class="facet__name"><button type="button" class="facet__toggle" data-facet-toggle aria-expanded="true">Type</button></legend><div class="facet__body" data-w="type"></div></fieldset>
            <fieldset class="facet"><legend class="facet__name"><button type="button" class="facet__toggle" data-facet-toggle aria-expanded="true">Price</button></legend><div class="facet__body" data-w="price"></div></fieldset>
          </div>
        </details>
      </aside>
      <div class="plp__main">
        <div data-ts-toolbar-slot></div>
        <div data-w="hits"></div>
        <div class="pagination" data-w="pagination"></div>
      </div>`;
    // The takeover shell used to ship a two-control toolbar of its own (stats +
    // a bare sort), which is why searching from a non-PLP page lost the sort
    // field/direction adapter, the items range, page size, the grid/list toggle
    // AND the filter chips — plp-toolbar.js bails when [data-plp-toolbar] is
    // absent. Clone the REAL toolbar instead: layout/theme.liquid renders
    // snippets/plp-toolbar.liquid into #PlpToolbarTpl on pages that can be
    // taken over, so there is still exactly one source of truth for its markup.
    const tpl = document.getElementById('PlpToolbarTpl');
    const slot = island.querySelector('[data-ts-toolbar-slot]');
    if (tpl && slot) {
      slot.replaceWith(tpl.content.cloneNode(true));
    } else if (slot) {
      // no template shipped: keep the old minimal toolbar rather than nothing
      slot.outerHTML = '<div class="plp__toolbar">'
        + '<span class="plp__result-note" data-w="stats"></span>'
        + '<label class="plp__sort"><span>Sort</span><span data-w="sort"></span></label></div>';
    }
    // mobile starts with the filter drawer CLOSED — an open facet stack
    // would bury the grid below a full screen of checkboxes
    if (matchMedia('(max-width: 980px)').matches) {
      const drawer = island.querySelector('.plp__drawer');
      if (drawer) drawer.removeAttribute('open');
    }
    root.appendChild(island);
    // plp-toolbar.js initialises on demand; the toolbar did not exist when it ran
    document.dispatchEvent(new CustomEvent('plp:shell'));
    }
    const q = (sel) => island.querySelector(sel);
    // the shell's server-rendered toolbar, kept so the cold-index bail-out can
    // put the Liquid page back the way it found it
    const ssrToolbar = adopted ? island.querySelector('.plp__toolbar') : null;
    const ssrToolbarHTML = ssrToolbar ? ssrToolbar.innerHTML : null;

    // A-2-style collapsible facet groups — the fieldsets are static island
    // chrome (widgets render into their [data-w] bodies), so state survives
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-facet-toggle]');
      if (!t) return;
      const closed = t.closest('.facet').classList.toggle('is-closed');
      t.setAttribute('aria-expanded', String(!closed));
    });

    // Deterministic URL → state restore: the vendored router's parse hooks
    // proved unreliable, so we build initialUiState ourselves; the router is
    // kept only for WRITING shareable URLs.
    function originalSearch() {
      try {
        const nav = performance.getEntriesByType('navigation')[0];
        if (nav && nav.name) return new URL(nav.name).search;
      } catch (e) { /* fall through */ }
      return location.search;
    }
    function stateFromURL() {
      // flat params (survive Shopify's redirect re-serialization, which
      // destroys nested bracket params): f=attr~v1^v2|attr2~v1  s=sort
      // q=query  pg=page  pr=min:max
      const ui = {};
      const sp = new URLSearchParams(originalSearch());
      const f = sp.get('f');
      if (f) {
        const rl = {};
        f.split('|').forEach((pair) => {
          const [attr, vals] = pair.split('~');
          if (attr && vals) rl[attr] = vals.split('^').map((v) => decodeURIComponent(v));
        });
        if (Object.keys(rl).length) ui.refinementList = rl;
      }
      // unknown/retired sort values (an old shared `s=title:asc` link, say)
      // are dropped rather than sent to Typesense, which would error the search
      const s = sp.get('s');
      if (s && isLiveSort(s)) ui.sortBy = collection + '/sort/' + s;
      if (sp.get('q')) ui.query = sp.get('q');
      if (sp.get('pg')) ui.page = Number(sp.get('pg')) || 1;
      if (sp.get('pr')) ui.range = { price: sp.get('pr').replace('-', ':') };
      return Object.keys(ui).length ? ui : null;
    }
    let restored = stateFromURL();
    const preTyped = ((document.querySelector('[data-usearch-input]') || {}).value || '').trim();
    if (preTyped) restored = Object.assign({}, restored || {}, { query: preTyped });

    const search = instantsearch({
      searchClient: adapter.searchClient,
      indexName: collection,
      future: { preserveSharedStateOnUnmount: true },
      initialUiState: restored ? { [collection]: restored } : undefined,
    });

    // Hand-rolled URL writer (replaces the routing option: the vendored router
    // never honoured its parse hooks, and Shopify's preview bar rewrites the
    // URL underneath it anyway). Foreign params on the live location survive
    // every write.
    let lastWritten = '';
    function writeURL() {
      const st = (search.getUiState() || {})[collection] || {};
      const params = new URLSearchParams();
      for (const [k, v] of new URLSearchParams(location.search)) {
        if (!['f', 's', 'q', 'pg', 'pr'].includes(k) && !k.startsWith('products')) params.append(k, v);
      }
      const rl = st.refinementList || {};
      // values go in RAW: params.toString() percent-encodes the whole f=
      // value once. Pre-encoding here double-encoded (%2520) every value
      // containing a space, breaking multi-word deep links on re-parse.
      const pairs = Object.keys(rl)
        .filter((attr) => (rl[attr] || []).length)
        .map((attr) => attr + '~' + rl[attr].join('^'));
      if (pairs.length) params.set('f', pairs.join('|'));
      if (st.sortBy && st.sortBy !== collection) params.set('s', st.sortBy.replace(collection + '/sort/', ''));
      if (st.query) params.set('q', st.query);
      if (st.page && st.page > 1) params.set('pg', String(st.page));
      if (st.range && st.range.price) params.set('pr', st.range.price.replace(':', '-'));
      const q = params.toString();
      const next = location.pathname + (q ? '?' + q : '');
      if (next !== lastWritten) {
        lastWritten = next;
        history.replaceState(history.state, '', next);
      }
    }
    search.on('render', writeURL);

    /* ---- hold one page either side --------------------------------------
     * Client pagination never navigates, so there is no document to prefetch:
     * "the next page" here is a Typesense query plus its images. Warm both.
     *
     * searchOnce() runs the query through the SAME searchClient without
     * touching widget state, so the adapter's own result cache
     * (cacheSearchResultsForSeconds: 120, set above) answers the real request
     * when the shopper presses Next. Images are warmed through a detached
     * <img> carrying the tile's exact srcset+sizes, so the browser resolves
     * the SAME candidate the tile will ask for — warming a fixed width would
     * have paid for a rendition nothing then used.
     *
     * Guarded on Save-Data and 2g, debounced so a burst of facet clicks fires
     * one pair of queries rather than one per render, and capped. */
    const warmedPages = new Set();
    let warmTimer = 0;
    function warmImages(content) {
      const hits = (content && content.hits) || [];
      for (let i = 0; i < hits.length && i < 8; i++) {
        const u = hits[i] && hits[i].image;
        if (!u) continue;
        const im = new Image();
        try { im.fetchPriority = 'low'; } catch (e) { /* not supported */ }
        im.sizes = GRID_SIZES;
        im.srcset = gridSrcset(u);
      }
    }
    function warmAdjacent() {
      const conn = navigator.connection || {};
      if (conn.saveData) return;
      if (conn.effectiveType && /(^|-)2g$/.test(conn.effectiveType)) return;
      if (navigator.webdriver || /bot|crawl|spider|slurp|lighthouse|headless/i.test(navigator.userAgent)) return;
      const helper = search.helper;
      if (!helper || typeof helper.searchOnce !== 'function') return;
      const st = (search.getUiState() || {})[collection] || {};
      const page = st.page || 1;
      const nbPages = (helper.lastResults && helper.lastResults.nbPages) || 0;
      [page - 1, page + 1].forEach((p) => {
        if (p < 1 || (nbPages && p > nbPages)) return;
        const key = JSON.stringify(Object.assign({}, st, { page: p }));
        if (warmedPages.has(key) || warmedPages.size > 40) return;
        warmedPages.add(key);
        let res;
        // helper pages are 0-based; the uiState `page` (and our pg= param) is 1-based
        try { res = helper.searchOnce({ page: p - 1 }); } catch (e) { return; }
        if (res && typeof res.then === 'function') res.then((r) => warmImages(r && r.content), () => {});
      });
    }
    search.on('render', () => { clearTimeout(warmTimer); warmTimer = setTimeout(warmAdjacent, 600); });
    /* The Brand facet is withheld when every product type in the result set
       is one whose brand is hidden (window.ST_BRAND_HIDDEN_TYPES, the list
       Liquid reads too), or when the index returned no vendor values at all
       (the Typesense app withholds the vendor of a hidden-brand product, so
       on a tiles collection the facet arrives empty). Luke, 2026-09-16, on the
       Tiles PLP: "I still see Brands in the filters for tiles, I need to not
       see those brands for Tiles at all." #303 put this rule in the React
       rail (src/islands/plp/Facets.jsx) — which only runs under ?plp=react;
       THIS is the rail every collection page actually mounts, so the rule
       lives here too. The fieldset stays in the DOM (the refinement widget
       owns its container); only its visibility changes, on every render, so
       a mixed search that adds a tap to the results gets Brand back. */
    search.on('render', () => {
      const body = q('[data-w="vendor"]');
      const fs = body && body.closest('fieldset');
      const res = search.helper && search.helper.lastResults;
      if (!fs || !res) return;
      const hidden = Array.isArray(window.ST_BRAND_HIDDEN_TYPES) ? window.ST_BRAND_HIDDEN_TYPES : [];
      let types = [], vendors = [];
      try { types = res.getFacetValues('product_type') || []; } catch (e) { types = []; }
      try { vendors = res.getFacetValues('vendor') || []; } catch (e) { vendors = []; }
      if (window.STBrand) vendors = vendors.filter((v) => !window.STBrand.named(v.name));
      const allHidden = hidden.length > 0 && types.length > 0 && types.every((t) => hidden.indexOf(t.name) !== -1);
      fs.hidden = allHidden || vendors.length === 0;
    });

    /* page size — the toolbar (assets/plp-toolbar.js) owns the control and the
     * localStorage value; the island owns the query. Kept out of the URL on
     * purpose: a `per=` param would double the adjacent-page prefetch space
     * for a preference that is not worth a crawlable URL. */
    document.addEventListener('plp:per', (e) => {
      const n = Number(e.detail);
      if (!n || !search.helper) return;
      // the control counts CELLS; the query counts products (cells - tiles)
      cellsPerPage = n;
      const per = productsPerPage(n);
      if (search.helper.state.hitsPerPage === per) return;
      search.helper.setQueryParameter('hitsPerPage', per).setPage(0).search();
    });
    // the toolbar may have booted before this island did; ask it to re-announce
    document.dispatchEvent(new CustomEvent('plp:ready'));

    /* THE CARD. Since 2026-09-17 the catalogue card is rendered by ONE
     * script for every live grid — assets/product-card.js (window.STCard):
     * this grid, the search dialog, and whatever comes next. The template
     * that lived here (star row, delivery line, badge row via STBadges, the
     * article markup) moved there byte for byte; InstantSearch accepts a
     * string-returning item template. hit.__position is the widget's own
     * 1-based position, which drives the eager/priority image hints. */
    const hitTemplate = (hit) => (window.STCard ? window.STCard.render(hit, hit.__position) : '');

    // Colour facet renders as circular swatches (.facet__sw); metallic finishes get
    // subtle gradients, unknown values a neutral chip (name stays in the title).
    // Live values come from the swatch_value metaobjects via window.ST_SWATCH
    // (snippets/swatch-map.liquid); this table is the unseeded-store fallback.
    // swatch fills (published metaobjects + the fallback table) live with
    // the card renderer now, so the facet dots and the tile dots agree
    const swatchFill = (attribute, value) => (window.STCard ? window.STCard.swatchFill(attribute, value) : '#cfccc6');

    const colourItem = (item, { html }) => html`
      <span class="facet__sw${item.isRefined ? ' is-on' : ''}" role="checkbox" aria-checked="${item.isRefined ? 'true' : 'false'}" style="--sw:${swatchFill('Colour', item.label)}" data-swatch-label="${item.label} (${item.count})" aria-label="${item.label}"></span>`;

    // span-based rows: the refinementList widget binds the click → refine on
    // each item container itself, so custom templates must not be links
    const facetItem = (item, { html }) => html`
      <span class="facet__value${item.isRefined ? ' is-on' : ''}">
        <span class="facet__box" aria-hidden="true"></span>
        <span class="facet__label">${item.label === 'true' ? 'In stock' : item.label === 'false' ? 'Out of stock' : item.label}</span>
        <span class="facet__count">${item.count}</span>
      </span>`;

    const refinement = (attribute, container, extra = {}) =>
      refinementList(Object.assign({
        container: q(container),
        attribute,
        limit: 8,
        showMore: true,
        showMoreLimit: 30,
        templates: { item: facetItem, showMoreText: (d, { html }) => html`<span class="facet__more">${d.isShowingMore ? 'Show less' : 'Show more'}</span>` },
      }, extra));

    // ---- universal header bar (v5): the island mounts its live searchBox
    // into the bar's slot, hides the plain submit input, and appends the
    // scope pill. usearch.js owns open/close/morph; we own the live query,
    // and we answer its hold-to-purge event by resetting all state. ----
    const usMount = document.querySelector('[data-usearch-slot]');
    const usFallback = document.querySelector('[data-usearch-input]');
    const collectionTitle = handle
      ? ((document.querySelector('.collection__head h1') || {}).textContent || handle).trim()
      : null;
    // configurator components never surface in a grid: availability:!=component
    // rides the same configure() the collection scope does. Gated on the
    // theme setting (window.TYPESENSE.availabilityFilter) because Typesense
    // errors the whole search on a filter field the index does not have yet.
    const availFilter = cfg.availabilityFilter ? 'availability:!=[component,in_store_only]' : '';
    const scopeFilters = (h) => [h ? `collections:=${JSON.stringify(h)}` : '', availFilter].filter(Boolean).join(' && ');
    let scopeWidget = (handle || availFilter)
      ? configure({ filters: scopeFilters(handle), hitsPerPage: productsPerPage(CELLS) })
      : configure({ hitsPerPage: productsPerPage(CELLS) });
    if (usMount) {
      usMount.hidden = false;
      if (usFallback) usFallback.hidden = true;
      const usForm = usMount.closest('form');
      if (usForm) usForm.addEventListener('submit', (e) => e.preventDefault()); // live island handles it
      document.addEventListener('usearch:purge', () => {
        // hold-to-purge: wipe query + every refinement in one move
        search.setUiState((ui) => ({ ...ui, [collection]: {} }));
      });
      // dialog mode (sections/header.liquid search_dialog): the header renders
      // a scope button ("in <collection>" / Everywhere menu, fused to the bar's
      // right end) server-side and search-dialog.js owns it, so the island's
      // own widen-to-store chip stays out of the way
      const dialogBar = usMount.closest('[data-usearch-dialog]');
      if (handle && !dialogBar) {
        const chip = h('button', 'usearch__scope', { type: 'button', 'aria-describedby': 'usearch-scope-pop' }, [
          'In: ', h('b', null, null, [collectionTitle]),
          h('span', 'usearch__scope-x', { 'aria-hidden': 'true' }, ['×']),
          h('span', 'usearch__scope-pop', { role: 'tooltip', id: 'usearch-scope-pop' }, [
            h('b', null, null, ['Searching in ' + collectionTitle]),
            'Only ' + collectionTitle + ' products are shown. Click × to search the whole store.',
          ]),
        ]);
        usMount.insertAdjacentElement('afterend', chip);
        chip.addEventListener('click', () => {
          search.removeWidgets([scopeWidget]);
          scopeWidget = availFilter ? configure({ filters: availFilter, hitsPerPage: productsPerPage(cellsPerPage) }) : configure({ hitsPerPage: productsPerPage(cellsPerPage) });
          search.addWidgets([scopeWidget]);
          chip.remove();
          const qin = usMount.querySelector('input');
          if (qin) { qin.placeholder = 'Search products…'; qin.focus(); }
        });
      }
    }
    const { searchBox } = instantsearch.widgets;

    /* ---- TYPE DEBOUNCE ----------------------------------------------------
     * MEASURED, 2026-09-21 (perf/search-as-you-type, scripts/perf/typing-probe.mjs
     * against production www.sydneytaps.com.au, /search?q=timberline, 1440,
     * no CPU throttle, an idle M-series Mac):
     *
     *   typing "timberline vanity" (17 keys, 120ms cadence)
     *     wall 60.4s · longtask busy 72.0s · 92 long tasks · worst 3.6s
     *     per keystroke: 534ms for the 1st, 7457ms for the 17th
     *   same keystrokes with the Typesense host blocked (page.route)
     *     wall 2.1s · longtask busy 0ms
     *
     * So every millisecond of it is this widget. `searchAsYouType: true` has
     * NO debounce of its own: each keystroke refines the helper, which fires a
     * multi_search AND runs a full synchronous InstantSearch render — 24 tiles
     * rebuilt from HTML strings, the whole facet rail re-rendered, a document-
     * wide style recalc (trace: UpdateLayoutTree 1968ms self / 33%,
     * EventDispatch 2057ms self / 34% over 10 keystrokes) and, because the
     * cards are new nodes every time, Shopify's web-pixel manager re-binding a
     * click + auxclick listener to every card link (measured: ~190 listeners
     * added per keystroke, 3211 over 17).
     *
     * queryHook is InstantSearch's own supported seam for this: the input
     * still repaints instantly (searchAsYouType owns the field), only the
     * REFINE is deferred, so nothing about relevance, sorting, filtering or
     * the markup changes — there are simply 1-2 searches for a typed phrase
     * instead of 17. Trailing edge only, and `refine` is called with the query
     * that was current when the timer fired, so an out-of-order paint is not
     * possible: there is only ever one pending refine.
     *
     * Clearing the box (empty query) refines immediately — waiting 220ms to
     * show the unfiltered grid reads as lag, and it costs nothing because the
     * unfiltered answer is the one already in the adapter cache.
     *
     * 220ms: the median inter-key gap of a competent typist is 120-180ms, so
     * 220 spans a word without ever feeling held back on the pause AFTER the
     * word. Matches DEBOUNCE in assets/search-dialog.js (same cluster, same
     * keystrokes — the two surfaces must not disagree).
     *
     * Full findings, controls and dead ends: docs/perf/2026-09-21-search-typing.md.
     * Held by scripts/search-typing-gate.mjs. */
    const TYPE_DEBOUNCE = 220;
    let qhTimer = 0;
    let qhPending = null;
    const flushQuery = () => {
      if (!qhPending) return;
      clearTimeout(qhTimer); qhTimer = 0;
      const p = qhPending; qhPending = null;
      p.refine(p.query);
    };
    const queryHook = (query, refine) => {
      clearTimeout(qhTimer);
      if (!String(query || '').trim()) { qhPending = null; refine(query); return; }
      qhPending = { query: query, refine: refine };
      qhTimer = setTimeout(flushQuery, TYPE_DEBOUNCE);
    };
    // Enter / leaving the field must not wait out the debounce: the shopper
    // has told us the query is finished.
    if (usMount) {
      usMount.addEventListener('keydown', (e) => { if (e.key === 'Enter') flushQuery(); });
      usMount.addEventListener('focusout', flushQuery);
    }

    // The active refinements as labels, read off the helper state so the
    // empty state and the chips can never disagree about what is applied.
    const prettyRefinement = (attr, v) => {
      if (attr === 'available') return String(v) === 'true' ? 'In stock' : 'Out of stock';
      if (attr === 'tags' && v === 'configurable') return 'Configurable products';
      return String(v);
    };
    const activeRefinementLabels = () => {
      const st = search.helper && search.helper.state;
      if (!st) return [];
      const out = [];
      const push = (attr, vals) => (vals || []).forEach((v) => out.push(prettyRefinement(attr, v)));
      Object.keys(st.disjunctiveFacetsRefinements || {}).forEach((a) => push(a, st.disjunctiveFacetsRefinements[a]));
      Object.keys(st.facetsRefinements || {}).forEach((a) => push(a, st.facetsRefinements[a]));
      const num = st.numericRefinements || {};
      Object.keys(num).forEach((a) => Object.keys(num[a] || {}).forEach((op) => {
        (num[a][op] || []).forEach((v) => out.push((op.indexOf('>') === 0 ? 'From $' : 'To $') + v));
      }));
      return out;
    };
    // "Clear filters" in the empty state: every refinement goes, the
    // collection scope (configure filters) and the query stay. Delegated so
    // it survives the hits widget re-rendering the button.
    island.addEventListener('click', (e) => {
      if (!e.target.closest('[data-plp-clear]')) return;
      e.preventDefault();
      search.helper.clearRefinements().search();
    });

    search.addWidgets([
      searchBox({
        container: (function () {
          if (!usMount) return island; // headerless edge case — mount at island root
          const slot = document.createElement('div');
          slot.className = 'usearch__slot';
          usMount.prepend(slot);
          return slot;
        })(),
        placeholder: handle ? 'Search within ' + collectionTitle + '…' : 'Search products…',
        showSubmit: false,
        showReset: true,
        searchAsYouType: true,
        queryHook,
      }),
      scopeWidget,
      clearRefinements({
        container: q('[data-w="clear"]'),
        templates: { resetLabel: () => 'Clear all filters' },
        cssClasses: { button: 'chip chip--clear', disabledButton: 'is-hidden' },
      }),
      // Active-filter chips. The SSR path renders these from collection.filters
      // but is switched off when ts_on, so without this a filtered PLP shows no
      // way to remove a SINGLE refinement — only the rail's blanket clear.
      // The collection scope rides on configure({ filters }) rather than a
      // refinement, so it never appears here and cannot be dropped by accident.
      instantsearch.connectors.connectCurrentRefinements((ro) => {
        const container = ro.widgetParams.container;
        if (!container) return;
        const flat = [];
        (ro.items || []).forEach((item) => {
          (item.refinements || []).forEach((r) => flat.push({ r: r, refine: item.refine }));
        });
        // Rebuild only when the SET changes. Re-rendering these nodes on every
        // island render is what made the sort menu swallow clicks: a click whose
        // mousedown and mouseup straddle a rebuild fires against a detached node.
        const sig = flat.map((x) => x.r.attribute + '\u0000' + x.r.label + '\u0000' + (x.r.operator || '')).join('|');
        if (container._sig === sig) return;
        container._sig = sig;
        container.hidden = flat.length === 0;
        container.innerHTML = '';
        if (!flat.length) return;
        container.appendChild(h('span', 'plp__chips-label', null, ['Filtered by']));
        const pretty = (r) => {
          if (r.attribute === 'available') return String(r.label) === 'true' ? 'In stock' : 'Out of stock';
          if (r.operator) return (r.operator.indexOf('>') === 0 ? 'From ' : 'To ') + r.label;
          return String(r.label);
        };
        flat.forEach((x) => {
          const b = h('button', 'chip', { type: 'button' }, [
            pretty(x.r), h('span', null, { 'aria-hidden': 'true' }, ['\u00d7']),
          ]);
          b.setAttribute('aria-label', 'Remove filter ' + pretty(x.r));
          b.addEventListener('click', () => x.refine(x.r));
          container.appendChild(b);
        });
        const clear = h('button', 'chip chip--clear', { type: 'button' }, ['Clear all']);
        clear.addEventListener('click', () => flat.forEach((x) => x.refine(x.r)));
        container.appendChild(clear);
      })({ container: q('[data-w="chips"]') }),
      refinement('available', '[data-w="available"]'),
      // configurable products filter: a tags refinement scoped to the one
      // value, so it URL-round-trips through the flat f= param like any facet.
      // NOTE: this limit is the LARGEST of any facet widget, so it becomes the
      // max_facet_values Typesense is asked for — snippets/search-provider.liquid
      // hard-codes 60 to predict the first request. Change one, change both.
      refinement('tags', '[data-w="features"]', {
        limit: 60,
        showMore: false,
        transformItems: (items) => items
          .filter((i) => i.value === 'configurable')
          .map((i) => Object.assign({}, i, { label: 'Configurable products', highlighted: 'Configurable products' })),
      }),
      // Brand blackout (Luke, 2026-09-23; docs/decisions/2026-09-23-tile-brand-blackout.md):
      // a blackout vendor is never offered as a filter value, on any page.
      // The Typesense app already blanks vendor on brand-hidden documents; this
      // holds if a document ever arrives unblanked.
      refinement('vendor', '[data-w="vendor"]', {
        transformItems: (items) => items.filter((i) => !(window.STBrand && window.STBrand.named(i.value))),
      }),
      refinement('colours', '[data-w="colours"]', {
        limit: 24,
        showMore: false,
        templates: { item: colourItem },
        cssClasses: { list: 'facet__swatches' },
      }),
      // Finish: the raw option values (Brushed Brass, Matte Black, Graphite...)
      // the home strip and the megamenu Colours chips drill on (f=finishes~X,
      // 2026-09-09). A refinement in the URL is only honoured for an attribute
      // that has a widget - without this group a finish deep link was
      // silently dropped and the page showed the whole collection. Colour
      // above stays the 20-bucket normalised facet.
      refinement('finishes', '[data-w="finishes"]'),
      refinement('product_type', '[data-w="type"]'),
      // A-2-style dual-handle slider when the vendored build ships it;
      // min/max inputs otherwise
      instantsearch.widgets.rangeSlider
        ? instantsearch.widgets.rangeSlider({
            container: q('[data-w="price"]'),
            attribute: 'price',
            pips: false,
            tooltips: { format: (v) => '$' + Math.round(v).toLocaleString() },
          })
        : rangeInput({
            container: q('[data-w="price"]'),
            attribute: 'price',
            cssClasses: { form: 'facet__price', input: 'facet__price-in', submit: 'btn btn--ghost facet__apply', separator: 'facet__price-dash' },
            templates: { separatorText: () => '–', submitText: () => 'Go' },
          }),
      stats({
        container: q('[data-w="stats"]'),
        // ONE count on the page: the header band's eyebrow, kept live here.
        // The toolbar container stays mounted (widget contract) but empty —
        // it duplicated the eyebrow at rest (2026-08-17 feedback).
        templates: { text: (d) => {
          // one render, one query: the heading and the title move with the
          // count, off the same results object the grid was drawn from
          syncQueryChrome(d.query);
          const eb = document.querySelector('.collection__head .eyebrow');
          if (eb) eb.textContent = d.nbHits.toLocaleString() + ' products';
          // ...and the toolbar's "Items X-Y of Z". Same widget, same nbHits,
          // so the range can never disagree with the grid under it — including
          // when the availability facet is on, which nbHits already reflects.
          const range = document.querySelector('[data-plp-range]');
          if (range) {
            if (!d.nbHits) range.textContent = '';
            else {
              const per = d.hitsPerPage || 24;
              const from = (d.page || 0) * per + 1;
              const to = Math.min(from + per - 1, d.nbHits);
              range.textContent = 'Items ' + from.toLocaleString() + '\u2013' + to.toLocaleString()
                + ' of ' + d.nbHits.toLocaleString();
            }
          }
          return '';
        } },
      }),
      // sort as a pcombo-style select menu (same language as the project
      // drawer's rich selects) instead of the native <select>
      instantsearch.connectors.connectSortBy((ro, isFirst) => {
        const container = ro.widgetParams.container;
        if (isFirst) {
          container.innerHTML = '<div class="pcombo pcombo--sort" data-sort-combo>' +
            '<button type="button" class="pcombo__btn" aria-haspopup="listbox" aria-expanded="false"><span data-sort-label></span></button>' +
            '<svg class="pcombo__caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9.5 6 6 6-6"/></svg>' +
            '<div class="pcombo__panel" role="listbox" hidden></div></div>';
          const rootEl = container.firstElementChild;
          const panel = rootEl.querySelector('.pcombo__panel');
          const btn = rootEl.querySelector('.pcombo__btn');
          const close = () => { panel.hidden = true; rootEl.classList.remove('is-open'); btn.setAttribute('aria-expanded', 'false'); };
          container.addEventListener('click', (e) => {
            if (e.target.closest('.pcombo__btn')) {
              const open = panel.hidden;
              panel.hidden = !open;
              rootEl.classList.toggle('is-open', open);
              btn.setAttribute('aria-expanded', String(open));
              return;
            }
            const opt = e.target.closest('[data-sort-value]');
            if (opt) { container._refine(opt.dataset.sortValue); close(); }
          });
          document.addEventListener('pointerdown', (e) => { if (!container.contains(e.target)) close(); });
          document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !panel.hidden) { close(); e.stopPropagation(); } }, true);
        }
        container._refine = ro.refine;
        const current = ro.options.find((o) => o.value === ro.currentRefinement) || ro.options[0];
        container.querySelector('[data-sort-label]').textContent = current ? current.label : '';
        container.querySelector('.pcombo__panel').innerHTML = ro.options.map((o) =>
          '<button type="button" class="pcombo__opt' + (o.value === ro.currentRefinement ? ' is-active' : '') + '" role="option" data-sort-value="' + o.value + '">' +
          '<span class="pcombo__name">' + o.label + '</span>' +
          '<span class="pcombo__tick" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg></span></button>').join('');
      })({
        container: q('[data-w="sort"]'),
        items: LIVE_SORTS.map((s) => ({
          label: s.label,
          value: s.value ? `${collection}/sort/${s.value}` : collection,
        })),
      }),
      hits({
        container: q('[data-w="hits"]'),
        // promos ride inside the widget's own item list rather than being
        // injected into its DOM afterwards, so preact keeps ownership of the
        // grid and re-splices them on every facet/sort/page render
        // Products a grid must never show, dropped client-side as a net under
        // the flag-gated server filter (availability:!=component above):
        // configurator components, and in-store-only products (unpublished
        // from the online store 2026-09-08; the index carries them until the
        // next full reindex). nbHits still counts them until that reindex.
        transformItems: (items, { results }) => splicePromos(
          items.filter((hit) => hit.availability !== 'component' && hit.availability !== 'in_store_only'),
          results,
        ),
        templates: {
          item: (hit, bindings) => (hit.__promo ? promoTemplate(hit.__promo, bindings, hit.__after) : hitTemplate(hit, bindings)),
          // "No results for X" when there IS an X, so the empty state names
          // the same query the heading above it just did; the filters wording
          // is what is actually true when the query is blank
          // Zero hits is a RESULT the island owns, not a reason to hand the
          // page back to Liquid (Luke 2026-09-09: a finish link that matched
          // nothing showed "0 PRODUCTS" over the unfiltered server grid with
          // no rail and the SSR pager). Name what was asked for and where,
          // and offer the way out; the rail stays so a single filter can be
          // dropped instead.
          empty: (r, { html }) => {
            const term = String((r && r.query) || '').trim();
            const labels = activeRefinementLabels();
            if (labels.length) {
              const where = collectionTitle ? ' in ' + collectionTitle : '';
              return html`<div class="plp__empty" data-plp-empty>
                <p class="h-md">No products match ${labels.join(' + ')}${where}.</p>
                <p class="plp__empty-note">Remove a filter in the rail, or clear them all.</p>
                <button type="button" class="btn" data-plp-clear>Clear filters</button>
              </div>`;
            }
            const copy = (qHead && term)
              ? fillQ(qHead.dataset.tplEmpty, term)
              : (collectionTitle ? 'No products in ' + collectionTitle + ' yet.' : 'Nothing matches those filters.');
            return html`<div class="plp__empty" data-plp-empty><p class="h-md">${copy}</p></div>`;
          },
        },
        cssClasses: { list: 'product-grid', item: 'plp-hit' },
      }),
      pagination({
        container: q('[data-w="pagination"]'),
        padding: 1,
        scrollTo: root,
      }),
    ]);

    // first meaningful render → swap SSR content for the live island.
    // CONTENT STABILITY: when the landing state is default (no filters/sort/
    // query in the URL), the SSR grid — which is correct content — stays as
    // the visible grid inside the island, and the island's own hit list only
    // takes over on the FIRST real interaction (filter/sort/search/page),
    // when a repaint is what the user asked for. Filtered/shared URLs and
    // search takeovers swap immediately, since there the island view is the
    // truth and SSR is not.
    let swapped = false;
    let standbyGrid = null;
    let standbyState = null;
    const relevantState = () => {
      const st = (search.getUiState() || {})[collection] || {};
      return JSON.stringify({ q: st.query || '', r: st.refinementList || {}, s: st.sortBy || '', p: st.page || 1, ra: st.range || {} });
    };
    search.on('render', () => {
      // InstantSearch renders TWICE per refine: an optimistic pass with the
      // OLD results, then the results pass. Acting on the optimistic pass was
      // the "filters, jumps, then jumps again" bug: never reveal or settle
      // while a request is in flight, and freeze the hits box height so the
      // footer cannot bounce between the two passes.
      const pending = !!(search.helper && typeof search.helper.hasPendingRequests === 'function' && search.helper.hasPendingRequests());
      const hitsBox = island.querySelector('[data-w="hits"]');
      if (hitsBox && swapped && !standbyGrid) {
        if (pending) hitsBox.style.minHeight = hitsBox.offsetHeight + 'px';
        else requestAnimationFrame(() => { hitsBox.style.minHeight = ''; });
      }
      if (swapped) {
        if (pending) return;
        // standby: reveal the island grid on the first state change
        if (standbyGrid && standbyState && relevantState() !== standbyState) {
          standbyGrid.remove();
          const ais = island.querySelector('[data-w="hits"]');
          if (ais) ais.style.display = '';
          standbyGrid = null; standbyState = null;
        }
        return;
      }
      // "the island has results" means the island's OWN hit list has them.
      // In the adopted shell the SSR grid lives inside the island too, so
      // counting cards across the root declared victory on the first (empty)
      // render and stripped the rail placeholders a frame before the counts
      // arrived — the rail collapsed to bare headings and sprang back.
      const haveResults = adopted
        ? !!(hitsBox && hitsBox.querySelector('.card, .plp__empty'))
        : island.querySelectorAll('.card, .plp__empty').length > 0;
      if (!haveResults || pending) return;
      // the Liquid grid: tagged in the adopted shell, hunted for in the built
      // island (where it is still an arbitrary child of the root)
      const findSsrGrid = () => {
        if (adopted) return root.querySelector('[data-ts-ssr-grid]');
        for (const el of Array.from(root.children)) {
          if (el === island) continue;
          const g = el.matches && el.matches('.product-grid') ? el : (el.querySelector ? el.querySelector('.product-grid') : null);
          if (g) return g;
        }
        return null;
      };
      const landedDefault = !location.search.match(/[?&](f|s|q|pg|pr)=/);
      // PRODUCT cards, not children. The Liquid grid's children are product
      // cards AND merchandising promo cells, and since PR #190 (0c83e07,
      // 2026-09-08) product-card.liquid renders NOTHING at all for a product
      // carrying custom.availability == 'component'. So "the Liquid grid has
      // children" stopped meaning "the Liquid grid has products": on
      // /collections/floor-standing-vanities every product on Liquid's page 1
      // is a configurator component, and the grid is two promo tiles and no
      // cards. Counting children there handed the page to an empty grid
      // (Luke 2026-09-14). Everything below asks how many PRODUCTS Liquid
      // actually painted.
      const productCards = (el) => (el ? el.querySelectorAll('.card').length : 0);
      // cold index: a freshly created collection the indexer hasn't seen yet
      // returns zero hits while the Liquid grid has real products. Taking
      // over would show "0 products" + an empty facet rail over a page that
      // visibly has products — keep the server-rendered page instead.
      // ONLY on a default landing: with a filter, sort, query or page in the
      // URL the Liquid grid is NOT the shopper's page (it ignores every one
      // of them), so zero hits there is the true answer and the island keeps
      // the page - rail, chips, count and its empty state - and the SSR grid
      // and pager go, exactly as on a hit. Before this gate a finish link
      // that matched nothing (f=colours~Brushed+Brass) fell through here and
      // showed "0 PRODUCTS" over 797 pages of unfiltered products with no
      // rail (Luke 2026-09-09). The Liquid grid is reserved for boot failure
      // (the catch at the bottom) and for this cold-index case.
      const islandEmpty = adopted
        ? !!(hitsBox && hitsBox.querySelector('.plp__empty'))
        : !!island.querySelector('.plp__empty');
      if (handle && islandEmpty && landedDefault) {
        const g = findSsrGrid();
        if (g && productCards(g)) {
          swapped = true;
          if (adopted) {
            // hand the shell back: detach everything the widgets own (they go
            // on rendering into the orphans, harmlessly) and restore the
            // server-rendered toolbar minus its now-inert sort control
            const rail = root.querySelector('.plp__rail');
            if (rail) rail.remove();
            island.querySelectorAll('[data-w="hits"], [data-w="pagination"]').forEach((el) => el.remove());
            if (ssrToolbar && ssrToolbarHTML !== null) {
              ssrToolbar.innerHTML = ssrToolbarHTML;
              const sortCtl = ssrToolbar.querySelector('.plp__sort');
              if (sortCtl) sortCtl.remove();
            }
            root.classList.add('plp--bare');
          } else {
            island.remove();
          }
          console.warn('[plp] collection absent from the search index - keeping the Liquid grid (reindex pending)');
          return;
        }
      }
      swapped = true;
      const ssrGrid = findSsrGrid();
      if (adopted) {
        // the shell already IS the island: nothing to reveal, only the
        // SSR-only duplicates to retire
        // grid, pager AND Liquid's own empty state: the island owns the whole
        // results area, and Liquid's verdict (0 published products) can differ
        // from the index's (still holding products unpublished since the last
        // reindex) - vanity-lights rendered "Nothing matches" over 3 cards
        const ssrPag = root.querySelector('[data-ts-ssr-pagination]');
        if (ssrPag) ssrPag.remove();
        const ssrEmpty = root.querySelector('[data-ts-ssr-empty]');
        if (ssrEmpty) ssrEmpty.remove();
        // placeholders out, mounts in — one frame, no double-rendered counts
        island.querySelectorAll('.facet__body--pending, [data-ts-ssr-note]').forEach((el) => el.remove());
        island.querySelectorAll('[data-w][hidden]').forEach((el) => { el.hidden = false; });
      } else {
        Array.from(root.children).forEach((el) => {
          if (el === island) return;
          if (landedDefault && ssrGrid && el.contains(ssrGrid)) ssrGrid.remove(); // detach before the wrapper goes
          el.remove();
        });
        island.style.display = 'contents';
      }
      root.classList.remove('plp--bare');
      root.classList.add('plp--ts');
      // STANDBY IS A STAND-IN, SO IT HAS TO STAND IN. Holding the Liquid grid
      // on a default landing is a content-stability trade: the shopper keeps
      // the already-painted server grid and the island repaints only when
      // asked. That trade is only honest while the Liquid grid shows at least
      // the products the island would have shown. When Liquid is SHORT the
      // shopper is looking at a page that is missing products the pager and
      // the facet counts both insist are there — at zero cards, an empty grid
      // under a populated rail and a "1 2 3" pager, which is exactly the page
      // Luke was handed. Paging re-entered through relevantState() and
      // revealed the island grid, which is why page 2 "fixed" it.
      // >= rather than ===, deliberately: Liquid renders in_store_only
      // products as cards and the island filters them off the index, so on
      // the 22 in-store-only brands Liquid is legitimately LONGER. Demanding
      // exact parity would repaint those collections on every landing and
      // throw away the content stability this branch exists to buy, to fix
      // nothing the shopper can see. Short is the bug; long is a known and
      // harmless difference.
      const ssrCards = productCards(ssrGrid);
      const islandCards = productCards(island.querySelector('[data-w="hits"]'));
      if (landedDefault && ssrGrid && ssrCards && ssrCards >= islandCards) {
        const ais = island.querySelector('[data-w="hits"]');
        if (ais) {
          ais.style.display = 'none';
          if (!adopted) ais.parentElement.insertBefore(ssrGrid, ais);
          standbyGrid = ssrGrid;
          standbyState = relevantState();
        }
      } else if (adopted && ssrGrid) {
        ssrGrid.remove();
      }
    });

    // stale-while-revalidate: the transport (window.__TSX) can answer a search
    // from sessionStorage before the network does. When the live answer finally
    // lands AND differs, it calls this; refresh() clears the client cache and
    // re-asks, which the transport now answers from the fresh copy — so this
    // costs one silent re-render and cannot loop.
    if (window.__TSX) {
      window.__TSX.onFresh = () => { try { search.refresh(); } catch (e) { /* not started yet */ } };
    }

    search.start();
  }

  // island failed — the Liquid fallback grid stays. Say so: a silent catch here
  // is indistinguishable from "the search index is empty".
  boot().catch((e) => { console.warn('[plp] island did not start - keeping the Liquid grid', e); });
  }

  const templateRoot = document.querySelector('[data-ts-plp]');
  if (templateRoot) runIsland(templateRoot);
  // in-place takeover from any page: usearch.js mounts a fresh root on the
  // first typed character and fires this event
  document.addEventListener('usearch:takeover', () => {
    const r = document.querySelector('[data-ts-plp]');
    if (r) runIsland(r);
  });
})();
